import { useEffect } from 'react';
import { setNavigationBlocked } from '../utils/navigationGuard';

const DEFAULT_MESSAGE =
  'Tenés cambios sin guardar en este presupuesto. Si salís ahora los vas a perder. ¿Querés continuar?';

/**
 * Warns before losing unsaved changes in a form, covering the two
 * navigation paths that need separate handling in this app (plain
 * `BrowserRouter`, see `utils/navigationGuard.ts` for why `useBlocker`
 * doesn't work here):
 *
 * 1. In-app SPA navigation (a Navbar menu item, a Cancelar button, any
 *    `navigate()` call anywhere) - via the app-wide `pushState` patch in
 *    `utils/navigationGuard.ts`, armed/disarmed here based on `isDirty`.
 * 2. Tab close, page refresh, or typing a new URL and hitting Enter - via
 *    the native `beforeunload` event, which `pushState`-patching can't see
 *    at all since none of those go through `pushState`.
 *
 * `isDirty` must reflect real dirty state (touched since load/last save),
 * not fire on a pristine, freshly-opened form - the caller owns that
 * computation entirely; this hook only wires it into both mechanisms.
 */
export function useUnsavedChangesGuard(isDirty: boolean, message: string = DEFAULT_MESSAGE): void {
  useEffect(() => {
    setNavigationBlocked(isDirty, message);
    return () => setNavigationBlocked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      // Requerido por Chrome para mostrar el prompt nativo - el texto en sí
      // no es configurable por el navegador, pero el listener solo se activa
      // si `isDirty` es true (formulario realmente tocado).
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);
}
